import { useEffect, useRef, useState, type ReactElement } from "react";

import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { cn } from "@/lib/utils";

type PdfjsModule = typeof import("pdfjs-dist");

let pdfjsModule: Promise<PdfjsModule> | null = null;

function loadPdfjs(): Promise<PdfjsModule> {
  pdfjsModule ??= import("pdfjs-dist").then((pdfjs) => {
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    return pdfjs;
  });
  return pdfjsModule;
}

/**
 * One PDF.js viewer that stays mounted. Changing `url` replaces the document
 * after the new pages are drawn, so the frame does not tear down between files.
 */
export function HubPdfDocumentView({
  url,
  className,
  title,
}: {
  url: string;
  className?: string;
  title: string;
}): ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef(0);
  const [phase, setPhase] = useState<"empty" | "ready" | "error">("empty");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    let cancelled = false;
    let loadingTask: { destroy: () => Promise<void> } | null = null;

    void (async () => {
      try {
        const pdfjs = await loadPdfjs();
        if (cancelled || requestId !== requestRef.current) return;
        const task = pdfjs.getDocument({ url });
        loadingTask = task;
        const document = await task.promise;
        if (cancelled || requestId !== requestRef.current) {
          await document.destroy();
          return;
        }
        const holder = window.document.createElement("div");
        for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
          const page = await document.getPage(pageNumber);
          if (cancelled || requestId !== requestRef.current) {
            await document.destroy();
            return;
          }
          const viewport = page.getViewport({ scale: 1.35 });
          const canvas = window.document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (context == null) continue;
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.className = "mx-auto mb-3 block max-w-full bg-white shadow-sm";
          await page.render({ canvasContext: context, viewport }).promise;
          holder.appendChild(canvas);
        }
        const host = hostRef.current;
        if (host != null && !cancelled && requestId === requestRef.current) {
          host.replaceChildren(...holder.childNodes);
          setPhase("ready");
          setErrorMessage(null);
        }
        await document.destroy();
      } catch (error) {
        if (cancelled || requestId !== requestRef.current) return;
        setPhase("error");
        setErrorMessage(error instanceof Error ? error.message : "Could not open this PDF.");
      }
    })();

    return () => {
      cancelled = true;
      void loadingTask?.destroy();
    };
  }, [url]);

  return (
    <div className={cn("relative min-h-0 overflow-auto bg-slate-200", className)} aria-label={title}>
      {phase === "empty" ? (
        <p className="px-3 py-8 text-center text-sm text-slate-600">Opening PDF…</p>
      ) : null}
      {phase === "error" ? (
        <p className="px-3 py-8 text-center text-sm text-rose-700">{errorMessage ?? "Could not open this PDF."}</p>
      ) : null}
      <div ref={hostRef} className="px-3 py-3" />
    </div>
  );
}
